import { useEffect, useRef, useState } from "react";

const DEFAULT_INTERVAL = 360_000;
const VERSION_ENDPOINT = "/assets/version.json";
const APP_START_VERSION = __APP_VERSION__;

const fetchAppVersion = async () => {
  const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch app version: ${response.status}`);
  }

  return response.json();
};

const useAppVersionCheck = (interval = DEFAULT_INTERVAL) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const isCheckingRef = useRef(false);
  const hasDetectedUpdateRef = useRef(false);

  useEffect(() => {
    if (!APP_START_VERSION) {
      return undefined;
    }

    let intervalId = null;

    const checkVersion = async () => {
      if (
        isCheckingRef.current ||
        hasDetectedUpdateRef.current ||
        document.visibilityState === "hidden"
      ) {
        return;
      }

      isCheckingRef.current = true;

      try {
        const data = await fetchAppVersion();

        if (data?.version && data.version !== APP_START_VERSION) {
          hasDetectedUpdateRef.current = true;
          setUpdateAvailable(true);
        }
      } catch (error) {
        // Ignore transient fetch failures and retry on the next cycle.
      } finally {
        isCheckingRef.current = false;
      }
    };

    checkVersion();
    intervalId = window.setInterval(checkVersion, interval);

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [interval]);

  return {
    updateAvailable,
    currentVersion: APP_START_VERSION,
  };
};

export default useAppVersionCheck;
